import { useRef, useState, useMemo } from "react";
import { RecommendedFillersPopover } from "./RecommendedFillersPopover";
import { recommendFillerCarpetJobs } from "../../utility/recommendFillerCarpetJobs";
import { formatYds } from "../../utility/installerCapacityRemaining";

// - - - - - - - - - -
// Cell-corner icon for issue #140 — clickable indicator that an installer
// has remaining carpet capacity on this day, with the popover hung off
// the icon's bounding rect.
//
// Owns its own popover open-state so the OptimizerGrid integration is a
// single render call per cell. Multiple icons across the grid each manage
// their own popover; outside-click on the document body closes whichever
// one is open (because each popover registers a global mousedown listener
// only while open). Opening a second icon implicitly closes the first via
// that outside-click handler — natural single-popover-at-a-time behavior
// without parent coordination.
//
// The recommender query runs LAZILY when the icon is clicked, not at render
// time. With many partial-capacity cells across the grid, running the query
// up-front would be wasted work — most cells are never clicked. Running on
// click is cheap (the active-queue docs are already in memory) and avoids
// the per-render overhead.

export const PartialCapacityIcon = ({
  capacityInfo,
  cellInstallerCandidate,
  cellDate,
  installerName,
  activeQueueDocs,
  furnitureSkuList,
  stepsSkuList,
  normalizePayroll,
}) => {
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  // Lazy-compute recommendations once on open, then memoize until the
  // active-queue data changes (polling refresh) or the cell-context shifts.
  // useMemo with `open` in deps so the popover doesn't keep recomputing on
  // every parent re-render while it's just sitting open.
  const recommendations = useMemo(() => {
    if (!open) return null;
    return recommendFillerCarpetJobs({
      cellInstallerCandidate,
      cellDate,
      remainingYds: capacityInfo?.remaining,
      remainingFurniture: capacityInfo?.remainingFurniture,
      remainingSteps: capacityInfo?.remainingSteps,
      remainingJobStops: capacityInfo?.remainingJobStops,
      remainingRepairStops: capacityInfo?.remainingRepairStops,
      activeQueueDocs,
      furnitureSkuList,
      stepsSkuList,
      normalizePayroll,
    });
  }, [
    open,
    cellInstallerCandidate,
    cellDate,
    capacityInfo?.remaining,
    capacityInfo?.remainingFurniture,
    capacityInfo?.remainingSteps,
    capacityInfo?.remainingJobStops,
    capacityInfo?.remainingRepairStops,
    activeQueueDocs,
    furnitureSkuList,
    stepsSkuList,
    normalizePayroll,
  ]);

  const handleClick = (e) => {
    // Prevent the cell's drag-drop pointer-down handler from firing.
    e.stopPropagation();
    e.preventDefault();
    const rect = buttonRef.current?.getBoundingClientRect();
    setAnchorRect(rect || null);
    setOpen((prev) => !prev);
  };

  const handleClose = () => setOpen(false);

  // Title text — provides at-a-glance capacity info on hover even before
  // opening the popover. Helps install managers triage which cells are
  // worth investigating.
  //
  // #157 (2026-05-22): "remaining" is now the next-job cap (per-job + extras-
  // budget model). Under the old daily-budget model the title said "X yds
  // remaining of Y"; under the new model that's redundant (remaining == cap
  // when in single-load mode). Phrasing tightened to "Next job: up to X yds".
  //
  // #155 (2026-05-22): icon also fires on the #138 next-day-empty cells, so
  // the title needs to read sensibly for both cases (carpetJobCount=0 means
  // the cell is empty; >=1 means partial). Same copy works either way.
  const titleText = capacityInfo
    ? `Capacity — next job up to ${formatYds(capacityInfo.remaining)} yds. Click for filler suggestions.`
    : "Click for filler suggestions";

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        // Prevent drag-drop on the parent cell from being canceled by the
        // icon's pointer events. The cell's onDragOver/onDrop should keep
        // working — we only intercept the click.
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        title={titleText}
        aria-label={titleText}
        data-testid="partial-capacity-icon"
        style={{
          position: "absolute",
          top: "4px",
          right: "4px",
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          border: "1.5px solid #dc2626",
          backgroundColor: "rgba(255, 255, 255, 0.92)",
          color: "#dc2626",
          fontSize: "0.75rem",
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          padding: 0,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.25)",
          zIndex: 5,
        }}
      >
        🔍
      </button>
      <RecommendedFillersPopover
        open={open}
        anchorRect={anchorRect}
        onClose={handleClose}
        installerName={installerName}
        cellDate={cellDate}
        capacityInfo={capacityInfo}
        recommendations={recommendations}
      />
    </>
  );
};

export default PartialCapacityIcon;
